import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, useNavigate, useParams, Link } from 'react-router-dom';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { getCardapio, getProdutoDetalhes, verifyToken, getClienteProfile, criarComanda, getProdutoUpsells, getComandaByHash, getComandaByMesa, getLojaInfo } from '../services/api';
import { useCarrinho } from '../services/CarrinhoContext';
import ProdutoModal from '../components/ProdutoModal';
import CarrinhoOffcanvas from '../components/CarrinhoOffcanvas';
import NavigationMenu from '../components/NavigationMenu';
import ProdutoCarousel from '../components/ProdutoCarousel';
import SEOMetaTags from '../components/SEOMetaTags';
import { Cart, InfoCircle, Dashboard, Truck, Bag, Shop, Search, XmarkCircleSolid } from 'iconoir-react';
import Lottie from 'lottie-react';
import buscaProdutosAnimation from '../assets/busca-produtos.json';
import LoginModal from '../components/LoginModal';
import PerfilOffcanvas from '../components/PerfilOffcanvas';
import MeusPedidosOffcanvas from '../components/MeusPedidosOffcanvas';

import { updateTempUser } from '../services/api';

import Loading from '../components/Loading';
import UpselModal from '../components/UpselModal';
import ConciergeModal from '../components/ConciergeModal';
import BalcaoInfoModal from '../components/BalcaoInfoModal';
import Footer from '../components/Footer';
import { formatPrice } from '../utils/currency';

// Função para converter URL do YouTube para embed
const getVideoEmbedUrl = (url) => {
  if (!url) return null;
  
  // YouTube
  const youtubeMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/);
  if (youtubeMatch) {
    return `https://www.youtube.com/embed/${youtubeMatch[1]}?autoplay=1&mute=1&loop=1&playlist=${youtubeMatch[1]}&controls=0&showinfo=0&rel=0&modestbranding=1`;
  }
  
  // Vimeo
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) {
    return `https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=1&muted=1&loop=1&controls=0`;
  }
  
  // URL direta de vídeo
  if (url.match(/\.(mp4|webm|ogg)$/i)) {
    return url;
  }
  
  return null;
};


function Cardapio({ brand, delivery = false, balcao = false, mesaData = null }) {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const { itens, total, setTipoEntrega, mesa, setMesa, setComanda, setUltimaComanda, setComandaExistente, comandaExistente, ultimaComanda, upselVisualizados, addUpselVisualizado, adicionarItem, nomeCliente, mesaCliente, setNomeCliente, setMesaCliente } = useCarrinho();
  

  
  const [cardapio, setCardapio] = useState({ categorias: [], destaques: [] });
  const [loading, setLoading] = useState(true);
  const [produtoSelecionado, setProdutoSelecionado] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showCarrinho, setShowCarrinho] = useState(false);
  const [categoriaAtiva, setCategoriaAtiva] = useState('todos');
  const [termoBusca, setTermoBusca] = useState('');
  const [showLogin, setShowLogin] = useState(false);
  const [showPerfil, setShowPerfil] = useState(false);
  const [showMeusPedidos, setShowMeusPedidos] = useState(false);
  const [usuario, setUsuario] = useState(null);
  const [forceNavUpdate, setForceNavUpdate] = useState(0);
  const [showUpselModal, setShowUpselModal] = useState(false);
  const [upsellProducts, setUpsellProducts] = useState([]);
  const [loadingUpsells, setLoadingUpsells] = useState(false);
  const [showConcierge, setShowConcierge] = useState(false);
  const [showBalcaoInfo, setShowBalcaoInfo] = useState(false);
  const [pedidosPendentes, setPedidosPendentes] = useState(0);
  const [hasVideos, setHasVideos] = useState(false);

  // Embaralhar destaques apenas uma vez quando o cardápio carregar
  const destaquesEmbaralhados = useMemo(() => {
    if (!cardapio.destaques) return [];
    return cardapio.destaques.map(destaque => ({
      ...destaque,
      produtos: [...(destaque.produtos || [])].sort(() => Math.random() - 0.5)
    }));
  }, [cardapio.destaques]);

  // Buscar pedidos pendentes quando usuário estiver logado
  useEffect(() => {
    if (!usuario) {
      setPedidosPendentes(0);
      return;
    }

    const buscarPedidosPendentes = async () => {
      const token = localStorage.getItem('sitri_token');
      if (!token) return;

      try {
        const { getComandasCliente } = await import('../services/api');
        const result = await getComandasCliente(brand, token);
        if (result?.success) {
          const pendentes = result.comandas.filter(cmd => {
            const status = (cmd.status || '').toLowerCase();
            return status !== 'fechada' && status !== 'cancelada' && status !== 'entregue';
          }).length;
          setPedidosPendentes(pendentes);
        }
      } catch (error) {
        // Silencioso
      }
    };

    buscarPedidosPendentes();
    const interval = setInterval(buscarPedidosPendentes, 30000);
    return () => clearInterval(interval);
  }, [usuario, brand]);

  const categoriasRefs = useRef({});
  const produtosContainerRef = useRef(null);
  const categoriasMenuRef = useRef(null);
  const categoriaBotaoRefs = useRef({});
  const todosBtnRef = useRef(null);
  const destaquesRef = useRef(null);

  const [visibleCategoriesCount, setVisibleCategoriesCount] = useState(999);

  const MAX_TITLE_LEN = 40;
  const MAX_DESC_LEN = 73;

  // Função para verificar se é dia de promoção
  const verifyPromoDays = (diasValue) => {
    if (!diasValue) return false;
    const d = new Date();
    const weekday = [1, 2, 4, 8, 16, 32, 64]; // Dom, Seg, Ter, Qua, Qui, Sex, Sab
    const currentDayBit = weekday[d.getDay()];
    const isPromoDay = !!(currentDayBit & diasValue);
    return isPromoDay;
  };

  // Função para calcular preço correto
  const calculatePrice = (produto, isDelivery) => {
    const basePrice = isDelivery ? (produto.valor_delivery ?? produto.valor) : produto.valor;
    const originalPrice = produto.valor_original ?? basePrice;
    let promoPrice = null;
    let isPromo = false;

    const promoFlag = (produto.em_promocao === true)
      || (produto.b_promocao === true || produto.b_promocao === 1 || produto.b_promocao === '1')
      || (produto.promocao_info && (produto.promocao_info.b_promocao === 1 || produto.promocao_info.b_promocao === true));

    const diasPromo = produto.dias_promocao ?? produto.promocao_info?.dias_promocao;

    if (isDelivery) {
      const hasDeliveryPromo = produto.b_promocao_delivery && produto.valor_promocional_delivery;
      const hasGeneralPromoValue = produto.promocao_info?.valor_promocional || produto.valor_promocional;

      if (hasDeliveryPromo) {
        const isPromoDay = produto.dias_promocao_delivery ? verifyPromoDays(produto.dias_promocao_delivery) : true;
        if (isPromoDay) {
          promoPrice = parseFloat(produto.valor_promocional_delivery);
          isPromo = true;
        }
      } else if (promoFlag && hasGeneralPromoValue) {
        const isPromoDay = diasPromo ? verifyPromoDays(diasPromo) : true;
        const val = produto.promocao_info?.valor_promocional ?? produto.valor_promocional;
        if (isPromoDay && val) {
          promoPrice = parseFloat(val);
          isPromo = true;
        }
      }
    } else {
      const hasPromoValue = produto.promocao_info?.valor_promocional || produto.valor_promocional;
      if (promoFlag && hasPromoValue) {
        const isPromoDay = diasPromo ? verifyPromoDays(diasPromo) : true;
        const val = produto.promocao_info?.valor_promocional ?? produto.valor_promocional;
        if (isPromoDay && val) {
          promoPrice = parseFloat(val);
          isPromo = true;
        }
      }
    }

    return {
      valor: isPromo ? promoPrice : basePrice,
      valor_original: originalPrice,
      em_promocao: isPromo
    };
  };
  
  // Verificar se deve mostrar apenas o cardápio (sem interação)
  const isDeliveryRoute = location.pathname.includes('/delivery');
  const isBalcaoRoute = location.pathname.includes('/balcao');
  const isMesaRoute = location.pathname.includes('/dinein/') || location.pathname.includes('/tablet/');
  const temMesa = mesaData !== null;
  const modoVisualizacao = !temMesa && !isDeliveryRoute && !isBalcaoRoute;

  useEffect(() => {
    carregarCardapio();
    configurarTipoEntrega();
    
    // Configurar mesa se vier de mesaData
    if (mesaData) {
      setMesa(mesaData);
    }
    
    // Verificar se há token na URL (OAuth)
    const urlParams = new URLSearchParams(window.location.search);
    const tokenFromUrl = urlParams.get('token');
    
    if (tokenFromUrl) {
      // Salvar token do OAuth
      localStorage.setItem('sitri_token', tokenFromUrl);
      
      // Limpar URL
      window.history.replaceState({}, '', window.location.pathname);
      
      // Verificar token imediatamente
      verificarToken();
    } else {
      // Carregar usuário do localStorage primeiro (para evitar flicker)
      const usuarioSalvo = localStorage.getItem('sitri_user');
      if (usuarioSalvo) {
        try {
          const userData = JSON.parse(usuarioSalvo);
          setUsuario(userData);
        } catch (error) {
        }
      }
      
      // Verificar token salvo (para validar se ainda é válido)
      verificarToken();
    }
    
    // Verificar se há hash na URL (WhatsApp)
    if (delivery && params.hash) {
      carregarComandaPorHash(params.hash);
    } else {
      // Carregar última comanda do localStorage
      carregarUltimaComanda();
    }
    
    // Para balcão sem mesa (rota /balcao), verificar se já tem nome e mesa salvos
    if (balcao && !mesaData) {
      const balcaoInfo = localStorage.getItem('sitri_balcao_info');
      if (balcaoInfo) {
        try {
          const { nome_cliente, mesa_cliente } = JSON.parse(balcaoInfo);
          if (nome_cliente && mesa_cliente) {
            setNomeCliente(nome_cliente);
            setMesaCliente(mesa_cliente);
          } else {
            setShowBalcaoInfo(true);
          }
        } catch (error) {
          setShowBalcaoInfo(true);
        }
      } else {
        setShowBalcaoInfo(true);
      }
    }
    
    // Listener para evento de upsell do carrinho
    const handleUpselEvent = (event) => {
      const { produto } = event.detail;
      if (produto) {
        abrirProduto(produto);
      }
    };
    
    window.addEventListener('openProdutoFromUpsell', handleUpselEvent);
    
    return () => {
      window.removeEventListener('openProdutoFromUpsell', handleUpselEvent);
    };
  }, [brand, delivery, balcao, params.hash]);
  
  // Abrir produto automaticamente se houver produtoId na URL
  useEffect(() => {
    if (params.produtoId && !loading && cardapio.loja && !showModal) {
      abrirProduto({ id: params.produtoId });
    }
  }, [params.produtoId, loading, cardapio.loja, showModal]);
  
  // Forçar update do NavigationMenu quando comandas mudarem (apenas IDs para evitar re-renders desnecessários)
  useEffect(() => {
    setForceNavUpdate(prev => prev + 1);
  }, [comandaExistente?.id, ultimaComanda?.id]);

  // Recarregar comanda da mesa após finalização de pedido
  useEffect(() => {
    const recarregarComandaMesa = async () => {
      if (mesa?.token) {
        try {
          const comandaMesa = await getComandaByMesa(brand, mesa.token);
          if (comandaMesa?.comanda && comandaMesa.comanda.status !== 'fechada' && comandaMesa.comanda.status !== 'entregue') {
            setComandaExistente(comandaMesa.comanda);
            setUltimaComanda(comandaMesa.comanda);
            setForceNavUpdate(prev => prev + 1);
          }
        } catch (error) {
          // Silencioso
        }
      }
    };

    window.addEventListener('pedidoFinalizado', recarregarComandaMesa);
    return () => window.removeEventListener('pedidoFinalizado', recarregarComandaMesa);
  }, [mesa?.token, brand, setComandaExistente, setUltimaComanda]);

  const [cartShake, setCartShake] = useState(false);
  const lastItemsCountRef = useRef(itens.length);
  const lastTotalRef = useRef(total);
  useEffect(() => {
    if (itens.length > lastItemsCountRef.current) {
      setCartShake(true);
      const t = setTimeout(() => setCartShake(false), 600);
      return () => clearTimeout(t);
    }
    lastItemsCountRef.current = itens.length;
  }, [itens.length]);
  useEffect(() => {
    if (total > lastTotalRef.current) {
      setCartShake(true);
      const t = setTimeout(() => setCartShake(false), 600);
      return () => clearTimeout(t);
    }
    lastTotalRef.current = total;
  }, [total]);

  const carregarComandaPorHash = async (hash) => {
    try {
      const comandaData = await getComandaByHash(brand, hash);
      
      if (comandaData) {
        
        // Converter produtos da comanda para itens do carrinho
        if (comandaData.pedidos && comandaData.pedidos.length > 0) {
          comandaData.pedidos.forEach(pedido => {
            pedido.produtos.forEach(produto => {
              const produtoCarrinho = {
                id: produto.id,
                nome: produto.nome,
                valor: produto.valor,
                imagem: produto.imagem
              };
              
              const variacoes = produto.variacao ? [produto.variacao] : [];
              const adicionaisFormatados = produto.adicionais || [];
              const opcionaisFormatados = produto.opcionais || [];
              
              
              // Adicionar cada quantidade individualmente
              for (let i = 0; i < produto.quantidade; i++) {
                adicionarItem(produtoCarrinho, variacoes, adicionaisFormatados, opcionaisFormatados);
              }
            });
          });
        }
        
        // Definir como comanda existente
        const comandaFormatada = {
          id: comandaData.id,
          status: comandaData.status,
          hash: hash,
          b_delivery: true
        };
        
        setComandaExistente(comandaFormatada);
        setUltimaComanda(comandaFormatada);
        
        // Salvar no localStorage
        localStorage.setItem('sitri_ultima_comanda', JSON.stringify(comandaFormatada));
        
      }
    } catch (error) {
      // Fallback para comportamento normal
      carregarUltimaComanda();
    }
  };

  const carregarUltimaComanda = () => {
    try {
      const comandaSalva = localStorage.getItem('sitri_ultima_comanda');
      if (comandaSalva) {
        const comanda = JSON.parse(comandaSalva);
        setUltimaComanda(comanda);
        
        // Se for mesa, também definir como comandaExistente
        if (mesaData || (!delivery && !balcao)) {
          setComandaExistente(comanda);
        }
      }
    } catch (error) {
      // Erro silencioso
    }
  };

  // Efeito para recarregar quando voltar da finalização
  useEffect(() => {
    const handleFocus = () => {
      carregarUltimaComanda();
      // Também verificar token quando a página ganhar foco
      verificarToken();
    };
    
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [mesaData, delivery, balcao]);
  
  // Efeito para verificar token periodicamente
  useEffect(() => {
    const interval = setInterval(() => {
      const token = localStorage.getItem('sitri_token');
      if (token && !usuario) {
        verificarToken();
      }
    }, 5000); // Verificar a cada 5 segundos
    
    return () => clearInterval(interval);
  }, [usuario]);



  const configurarTipoEntrega = () => {
    if (delivery) {
      setTipoEntrega('delivery');
    } else if (balcao) {
      setTipoEntrega('balcao');
    } else {
      setTipoEntrega('local');
    }
  };

  const buscarComandasAposLogin = async () => {
    const token = localStorage.getItem('sitri_token');
    if (!token) return;
    
    try {
      const { getComandasCliente } = await import('../services/api');
      const resultado = await getComandasCliente(brand, token);
      
      if (resultado && resultado.success && resultado.comandas && resultado.comandas.length > 0) {
        // Pegar a comanda mais recente para mostrar o link "PEDIDOS"
        const comandaMaisRecente = resultado.comandas[0];
        
        const comandaFormatada = {
          id: comandaMaisRecente.id,
          status: comandaMaisRecente.status,
          b_delivery: comandaMaisRecente.b_delivery,
          b_takeaway: comandaMaisRecente.b_takeaway,
          b_balcao: comandaMaisRecente.b_balcao,
          created_at: comandaMaisRecente.created_at
        };
        
        // Definir como última comanda para mostrar o link "PEDIDOS"
        setUltimaComanda(comandaFormatada);
        
      }
    } catch (error) {
    }
  };

  const verificarToken = async () => {
    const token = localStorage.getItem('sitri_token');
    
    if (token) {
      try {
        const resultado = await verifyToken(token);
        
        if (resultado && resultado.success && resultado.usuario) {
          // Verificar se usuário precisa completar dados
          if (resultado.usuario.newUser) {
            // Login direto
            setUsuario(resultado.usuario);
            localStorage.setItem('sitri_user', JSON.stringify(resultado.usuario));
            await buscarComandasAposLogin();
          } else {
            setUsuario(resultado.usuario);
            // Garantir que os dados do usuário estejam salvos no localStorage
            localStorage.setItem('sitri_user', JSON.stringify(resultado.usuario));
            
            // Buscar comandas do cliente para mostrar link "PEDIDOS"
            await buscarComandasAposLogin();
          }
        } else {
          // Token inválido, remover
          localStorage.removeItem('sitri_token');
          localStorage.removeItem('sitri_user');
          setUsuario(null);
        }
      } catch (error) {
        localStorage.removeItem('sitri_token');
        localStorage.removeItem('sitri_user');
      }
    } else {
    }
  };

  const carregarCardapio = async () => {
    try {
      setLoading(true);

      // Verificar fechamento temporário para rotas de pedido (delivery/balcão)
      if (delivery || balcao) {
        try {
          const lojaRes = await getLojaInfo(brand);
          if (lojaRes?.loja?.fechamento_temporario?.ativo) {
            navigate('/', { replace: true });
            return;
          }
        } catch (e) { /* continuar normalmente se falhar */ }
      }

      const data = await getCardapio(brand, delivery);
      setCardapio(data);
      
      // Verificar se existem produtos com vídeo
      const temVideos = data.categorias?.some(categoria => 
        categoria.produtos?.some(produto => produto.video)
      ) || false;
      setHasVideos(temVideos);
      
      // Armazenar info da loja globalmente para o footer
      if (data.loja) {
        window.lojaInfo = data.loja;
      }
    } catch (error) {
      // Erro silencioso
    } finally {
      setLoading(false);
    }
  };

  const abrirProduto = async (produto) => {
    try {
      const detalhes = await getProdutoDetalhes(brand, produto.id);
      setProdutoSelecionado(detalhes);
      setShowModal(true);
    } catch (error) {
      // Erro silencioso
    }
  };

  const fecharModal = () => {
    setShowModal(false);
    setProdutoSelecionado(null);
    
    // Se veio de um link direto, voltar para a rota base
    if (params.produtoId) {
      if (delivery) {
        navigate('/delivery', { replace: true });
      } else if (balcao) {
        navigate('/balcao', { replace: true });
      } else {
        navigate('/', { replace: true });
      }
    }
  };

  const abrirCarrinho = () => {
    setShowCarrinho(true);
  };

  const fecharCarrinho = () => {
    setShowCarrinho(false);
  };

  const handleMenuClick = (action) => {
    switch(action) {
      case 'cardapio':
        fecharCarrinho();
        setShowMeusPedidos(false);
        setShowPerfil(false);
        setShowLogin(false);
        break;
      case 'loja':
        navigate('/loja');
        break;
      case 'videos':
        if (location.pathname.includes('/delivery')) {
          navigate('/delivery/videos', { state: { from: location.pathname } });
        } else if (location.pathname.includes('/balcao')) {
          navigate('/balcao/videos', { state: { from: location.pathname } });
        } else if (location.pathname.match(/^\/dinein\/[^/]+/)) {
          const token = location.pathname.split('/dinein/')[1]?.split('/')[0];
          navigate(`/dinein/${token}/videos`, { state: { from: location.pathname } });
        } else if (location.pathname.match(/^\/tablet\/[^/]+/)) {
          const token = location.pathname.split('/tablet/')[1]?.split('/')[0];
          navigate(`/tablet/${token}/videos`, { state: { from: location.pathname } });
        } else {
          navigate('/videos', { state: { from: location.pathname } });
        }
        break;
      case 'destaques':
        setShowMeusPedidos(false);
        fecharCarrinho();
        setShowPerfil(false);
        setShowLogin(false);
        if (destaquesRef.current) {
          const top = destaquesRef.current.getBoundingClientRect().top + window.pageYOffset - 60;
          window.scrollTo({ top, behavior: 'smooth' });
        }
        break;
      case 'meusPedidos':
        setShowMeusPedidos(true);
        fecharCarrinho();
        setShowPerfil(false);
        setShowLogin(false);
        break;
      case 'perfil':
        if (usuario) {
          setShowPerfil(true);
          setShowLogin(false);
        } else {
          setShowPerfil(false);
          setShowLogin(true);
        }
        setShowMeusPedidos(false);
        fecharCarrinho();
        break;
      case 'concierge':
        setShowConcierge(true);
        break;
      default:
        console.warn('⚠️ [CARDAPIO] Ação desconhecida:', action);
    }
    
  };

  const handleLogin = async (userData) => {
    setUsuario(userData);
    localStorage.setItem('sitri_user', JSON.stringify(userData));
    
    // Após login, buscar comandas do cliente para mostrar link "PEDIDOS"
    await buscarComandasAposLogin();
  };

  const handleLogout = () => {
    setUsuario(null);
    localStorage.removeItem('sitri_user');
    localStorage.removeItem('sitri_token');
    localStorage.removeItem('sitri_user_data_updated');
    localStorage.removeItem('sitri_force_user_data');
  };

  const handleSaveBalcaoInfo = (info) => {
    setNomeCliente(info.nome_cliente);
    setMesaCliente(info.mesa_cliente);
    
    // Salvar no localStorage para não pedir novamente
    localStorage.setItem('sitri_balcao_info', JSON.stringify(info));
    
    setShowBalcaoInfo(false);
  };



  const carregarPerfilCompleto = async () => {
    const token = localStorage.getItem('sitri_token');
    if (token) {
      try {
        const perfil = await getClienteProfile(token);
        if (perfil && perfil.success) {
          // Aqui você pode usar os dados de endereços e cartões
        }
      } catch (error) {
        // Erro silencioso
      }
    }
  };

  const buscarUpsellsCarrinho = async () => {
    if (itens.length === 0) {
      abrirCarrinho();
      setShowMeusPedidos(false);
      return;
    }

    
    try {
      setLoadingUpsells(true);
      const todosUpsells = [];
      
      // Buscar upsells para cada produto no carrinho que ainda não foi visualizado
      for (const item of itens) {
        if (!upselVisualizados.includes(item.produto.id)) {
          const response = await getProdutoUpsells(brand, item.produto.id);
          
          if (response && response.success && response.data && Array.isArray(response.data) && response.data.length > 0) {
            todosUpsells.push(...response.data);
            addUpselVisualizado(item.produto.id);
          }
        }
      }
      
      // Remover duplicatas
      const upsellsUnicos = todosUpsells.filter((upsel, index, self) => 
        index === self.findIndex(u => u.id === upsel.id)
      );
      
      if (upsellsUnicos.length > 0) {
        setUpsellProducts(upsellsUnicos);
        setShowUpselModal(true);
      } else {
        abrirCarrinho();
        setShowMeusPedidos(false);
      }
    } catch (error) {
      abrirCarrinho();
      setShowMeusPedidos(false);
    } finally {
      setLoadingUpsells(false);
    }
  };

  const filtrarProdutos = () => {
    let produtos = [];
    if (categoriaAtiva === 'todos') {
      // Juntar todos os produtos de todas as categorias
      produtos = cardapio.categorias?.flatMap(cat => cat.produtos || []) || [];
    } else {
      const categoria = cardapio.categorias?.find(cat => cat.id === categoriaAtiva);
      produtos = categoria?.produtos || [];
    }
    
    if (termoBusca) {
      produtos = produtos.filter(produto => 
        produto.nome.toLowerCase().includes(termoBusca.toLowerCase()) ||
        produto.descricao.toLowerCase().includes(termoBusca.toLowerCase())
      );
    }
    
    return produtos;
  };

  const selecionarCategoria = (categoriaId) => {
    setCategoriaAtiva(categoriaId);
    
    if (categoriaId === 'todos') {
      const top = (produtosContainerRef.current?.offsetTop || 0) - 80;
      window.scrollTo({ top, behavior: 'smooth' });
      return;
    }
    
    const el = categoriasRefs.current[categoriaId];
    if (el) {
      const rectTop = el.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({ top: rectTop - 80, behavior: 'smooth' });
    }
  };

  const todasCategorias = cardapio.categorias || [];
  const getParentId = (cat) => cat.parentId ?? cat.parent_id ?? cat.id_pai ?? cat.categoria_mae ?? cat.mae_id ?? null;
  const isSubcategoria = (cat) => {
    const pid = getParentId(cat);
    return pid !== null && pid !== undefined && String(pid) !== '';
  };
  const categoriasRaiz = todasCategorias.filter((cat) => !isSubcategoria(cat));
  const categoriasRaizVisiveis = categoriasRaiz.filter((categoria) => {
    const parentProdutos = categoria.produtos || [];
    const subcats = todasCategorias.filter((c) => isSubcategoria(c) && String(getParentId(c)) === String(categoria.id));
    const subProdutos = subcats.flatMap((c) => c.produtos || []);
    const todosProdutos = [...parentProdutos, ...subProdutos];
    return todosProdutos.length > 0;
  });







  useEffect(() => {
    const container = categoriasMenuRef.current;
    const el = String(categoriaAtiva) === 'todos' ? todosBtnRef.current : categoriaBotaoRefs.current[categoriaAtiva];
    if (!container || !el) return;
    const left = el.offsetLeft - container.offsetLeft;
    const width = el.offsetWidth;
    const target = left - (container.clientWidth - width) / 2;
    container.scrollTo({ left: target, behavior: 'smooth' });
  }, [categoriaAtiva]);

  if (loading) {
    return <Loading message="Carregando cardápio..." />;
  }

  return (
    <div className="cardapio-page">
      <SEOMetaTags loja={cardapio.loja} brand={brand} />

      {/* Menu de Categorias */}
      <div className="categorias-container" style={{ position: 'sticky', top: 0, zIndex: 1020, backgroundColor: '#f3f4f6', borderBottom: '1px solid #e5e7eb', boxShadow: '0 2px 6px rgba(0,0,0,0.03)' }}>
        <div className="categorias-content">
          <div className="categorias-topbar">
            <div className="categorias-brand">
              {location.pathname.match(/^\/tablet\/[^/]+/) ? (
                <div className="categorias-logo-link" title={cardapio.loja?.nome || 'Loja'}>
                  <img
                    src={cardapio.loja?.logo_url ? `https://grubupload.s3.amazonaws.com/${cardapio.loja.logo_url}` : 'https://grubicons.s3.us-east-1.amazonaws.com/foto-padrao-grub-digital.jpg'}
                    alt={cardapio.loja?.nome || 'Logo da loja'}
                    className="categorias-logo"
                    onError={(e) => { e.target.src = 'https://grubicons.s3.us-east-1.amazonaws.com/foto-padrao-grub-digital.jpg'; }}
                  />
                </div>
              ) : (
                <Link to="/loja" className="categorias-logo-link" title={cardapio.loja?.nome || 'Loja'}>
                  <img
                    src={cardapio.loja?.logo_url ? `https://grubupload.s3.amazonaws.com/${cardapio.loja.logo_url}` : 'https://grubicons.s3.us-east-1.amazonaws.com/foto-padrao-grub-digital.jpg'}
                    alt={cardapio.loja?.nome || 'Logo da loja'}
                    className="categorias-logo"
                    onError={(e) => { e.target.src = 'https://grubicons.s3.us-east-1.amazonaws.com/foto-padrao-grub-digital.jpg'; }}
                  />
                </Link>
              )}
              <div className="categorias-loja-nome" style={{ display: 'flex', alignItems: 'center' }}>
                {cardapio.loja?.nome}
                {(location.pathname.match(/^\/tablet\/[^/]+/) || location.pathname.match(/^\/dinein\/[^/]+/)) && mesa?.identificador && (
                  <span 
                    className="badge ms-2" 
                    style={{
                      backgroundColor: '#eff1f5',
                      color: 'gray',
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      padding: '4px 10px',
                      borderRadius: '6px'
                    }}
                  >
                    MESA {mesa.identificador}
                  </span>
                )}
              </div>
            </div>
            <NavigationMenu 
              key={`nav-${forceNavUpdate}`}
              onMenuClick={handleMenuClick} 
              usuario={usuario} 
              hasHighlights={!!(cardapio.destaques && cardapio.destaques.length > 0)}
              lojaLogoUrl={cardapio.loja?.logo_url}
              loja={cardapio.loja}
              isTabletMode={location.pathname.match(/^\/tablet\/[^/]+/)}
              hasVideos={hasVideos}
            />
          </div>
          <div className="categorias-inner">
            <div className="categorias-menu" ref={categoriasMenuRef}>
            <button
              className={`categoria-btn ${categoriaAtiva === 'todos' ? 'active' : ''}`}
              onMouseDown={(e) => {
                e.preventDefault();
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                selecionarCategoria('todos');
              }}
              ref={todosBtnRef}
              type="button"
            >
              TODOS
            </button>
            {categoriasRaizVisiveis && categoriasRaizVisiveis.map(categoria => (
              <button
                key={categoria.id}
                className={`categoria-btn ${String(categoriaAtiva) === String(categoria.id) ? 'active' : ''}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  selecionarCategoria(categoria.id);
                }}
                ref={(el) => (categoriaBotaoRefs.current[categoria.id] = el)}
                type="button"
              >
                {categoria.nome.toUpperCase()}
              </button>
            ))}
            </div>
          </div>
        </div>
      </div>



      {/* Carousels de Produtos Destaque */}
      {destaquesEmbaralhados && destaquesEmbaralhados.length > 0 && (
        <div ref={destaquesRef} className="highlight-section" style={{ backgroundColor: '#111111', padding: '1px 30px 30px 30px' }}>
          <div className="produtos-container" style={{ paddingTop: 0, paddingBottom: 0 }}>
            {destaquesEmbaralhados.map((destaque, idx) => (
              destaque.produtos && destaque.produtos.length > 0 && (
                <ProdutoCarousel
                  key={destaque.id}
                  produtos={destaque.produtos.map(produto => {
                    const priceInfo = calculatePrice(produto, delivery);
                    const categoriaEncontrada = (cardapio.categorias || []).find(cat => (cat.produtos || []).some(p => String(p.id) === String(produto.id)));
                    return {
                      ...produto,
                      valor: priceInfo.valor,
                      valor_original: priceInfo.valor_original,
                      em_promocao: priceInfo.em_promocao,
                      categoria_nome: categoriaEncontrada ? categoriaEncontrada.nome : ''
                    };
                  })}
                  onProdutoClick={abrirProduto}
                  titulo={destaque.nome}
                  titleColor={'#d1d5db'}
                  variant={idx === 0 ? 'principal' : idx === 1 ? 'secundario' : 'terciario'}
                />
              )
            ))}
          </div>
        </div>
      )}

      <div className="search-section">
        <div className="search-wrapper">
          <Search style={{color:'#9ca3af', width:'22px', height:'22px'}} />
          <div className="search-input-wrapper">
            <input
              type="text"
              className="search-input"
              placeholder="Buscar produtos"
              value={termoBusca}
              onChange={(e) => setTermoBusca(e.target.value)}
            />
            {termoBusca && (
              <button
                type="button"
                className="search-clear-btn"
                title="Limpar busca"
                onClick={() => setTermoBusca('')}
              >
                <XmarkCircleSolid style={{width:'18px', height:'18px'}} />
              </button>
            )}
          </div>
        </div>
        {termoBusca && (
          <div className="search-results-info">
            {(() => {
              const t = (termoBusca || '').toLowerCase();
              const count = (cardapio.categorias || []).reduce((acc, cat) => {
                const produtos = cat.produtos || [];
                const matches = produtos.filter(p => (p.nome || '').toLowerCase().includes(t) || (p.descricao || '').toLowerCase().includes(t)).length;
                return acc + matches;
              }, 0);
              return `${count} resultado${count === 1 ? '' : 's'} para “${termoBusca}”`;
            })()}
          </div>
        )}
      </div>
      <div className="produtos-container" ref={produtosContainerRef}>
        {categoriasRaizVisiveis && categoriasRaizVisiveis.length > 0 ? (
          categoriasRaizVisiveis.map((categoria) => {
            const termo = termoBusca?.toLowerCase() || '';
            const parentProdutos = (categoria.produtos || []).filter((produto) => {
              if (!termo) return true;
              return (
                produto.nome?.toLowerCase().includes(termo) ||
                produto.descricao?.toLowerCase().includes(termo)
              );
            });
            const subcats = todasCategorias.filter((c) => isSubcategoria(c) && String(getParentId(c)) === String(categoria.id));
            const subcatsComProdutos = subcats.map((sub) => ({
              sub,
              produtos: (sub.produtos || []).filter((produto) => {
                if (!termo) return true;
                return (
                  produto.nome?.toLowerCase().includes(termo) ||
                  produto.descricao?.toLowerCase().includes(termo)
                );
              })
            })).filter((entry) => entry.produtos.length > 0);
            const temProdutos = parentProdutos.length > 0 || subcatsComProdutos.length > 0;
            if (!temProdutos) return null;
            return (
              <section
                key={categoria.id}
                data-catid={String(categoria.id)}
                ref={(el) => (categoriasRefs.current[categoria.id] = el)}
                style={{ paddingTop: '8px' }}
              >
                <div className="container">
                  <div className="categoria-heading">{String(categoria.nome || '').toUpperCase()}</div>
                  
                  {parentProdutos.length > 0 && (
                    <div className="produtos-grid">
                      {parentProdutos.map((produto) => (
                        <div
                          key={produto.id}
                          className="produto-card"
                          onClick={() => abrirProduto(produto)}
                        >
                          <div className="produto-layout">
                              <div className="produto-imagem-section" style={{position:'relative'}}>
                                {false ? (() => {
                                  const embedUrl = getVideoEmbedUrl(produto.video);
                                  const isDirectVideo = embedUrl && embedUrl.match(/\.(mp4|webm|ogg)$/i);
                                  
                                  if (isDirectVideo) {
                                    return (
                                      <video
                                        src={embedUrl}
                                        alt={produto.nome}
                                        muted
                                        loop
                                        autoPlay
                                        playsInline
                                        disablePictureInPicture
                                        controlsList="nodownload nofullscreen noremoteplayback"
                                        className="produto-imagem"
                                        style={{ objectFit: 'cover' }}
                                        onError={(e) => {
                                          e.target.style.display = 'none';
                                          e.target.nextElementSibling.style.display = 'block';
                                        }}
                                      />
                                    );
                                  } else if (embedUrl) {
                                    return (
                                      <iframe
                                        src={embedUrl}
                                        title={produto.nome}
                                        className="produto-imagem"
                                        style={{
                                          border: 'none',
                                          width: '100%',
                                          height: '100%'
                                        }}
                                        allow="autoplay; encrypted-media"
                                        allowFullScreen={false}
                                      />
                                    );
                                  }
                                  return null;
                                })() : null}
                                <img
                                  src={produto.imagem ? `https://grubupload.s3.amazonaws.com/${produto.imagem}` : 'https://grubicons.s3.us-east-1.amazonaws.com/foto-padrao-grub-digital.jpg'}
                                  alt={produto.nome}
                                  className="produto-imagem"
                                  
                                  onError={(e) => {
                                    e.target.src = 'https://grubicons.s3.us-east-1.amazonaws.com/foto-padrao-grub-digital.jpg';
                                  }}
                                />
                                {produto.video && (
                                  <div style={{
                                    position: 'absolute',
                                    top: '8px',
                                    left: '8px',
                                    background: 'rgba(0, 0, 0, 0.7)',
                                    borderRadius: '12px',
                                    padding: '4px 8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '4px',
                                    color: '#fff',
                                    fontSize: '9px',
                                    fontWeight: '600',
                                    pointerEvents: 'none'
                                  }}>
                                    <span style={{ fontSize: '10px' }}>▶</span>
                                    VÍDEO
                                  </div>
                                )}
                                {((produto.em_promocao === true) || (produto.promocao_info?.b_promocao === 1) || (produto.promocao_info?.b_promocao === '1') || (produto.b_promocao === 1) || (produto.b_promocao === '1') || (produto.b_promocao === true)) && (
                                  <div style={{
                                    position:'absolute',
                                    left:0,
                                    right:0,
                                    bottom:0,
                                    background:'#dc2626',
                                    color:'#ffffff',
                                    textAlign:'center',
                                    padding:'6px 0',
                                    fontSize:'0.8rem',
                                    fontWeight:700
                                  }}>EM PROMOÇÃO</div>
                                )}
                              </div>
                          <div className="produto-content">
                           
                            <h3 className="produto-titulo">{produto.nome}</h3>
                            <p className="produto-descricao">{produto.descricao}</p>
                            {(() => {
                              const pricing = calculatePrice(produto, delivery);
                              return (
                                <div className="produto-preco">
                                  {pricing.em_promocao
                                    ? (
                                      <>
                                        <div style={{color:'#6b7280', fontWeight:400, fontSize:'0.85rem', textDecoration:'line-through', marginBottom:'2px'}}>
                                          {produto.variacao ? `A partir de ${formatPrice(pricing.valor_original)}` : formatPrice(pricing.valor_original)}
                                        </div>
                                        <div style={{color:'#10b981', fontWeight:700}}>
                                          {produto.variacao ? `A partir de ${formatPrice(pricing.valor)}` : formatPrice(pricing.valor)}
                                        </div>
                                      </>
                                    )
                                    : (
                                      produto.variacao ? `A partir de ${formatPrice(pricing.valor)}` : formatPrice(pricing.valor)
                                    )
                                  }
                                </div>
                              );
                            })()}
                          </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {subcatsComProdutos.map(({ sub, produtos }) => (
                    <div key={sub.id} style={{ marginTop: '18px' }}>
                      <div className="subcategoria-heading">{String(sub.nome || '').toUpperCase()}</div>
                      
                      <div className="produtos-grid">
                        {produtos.map((produto) => (
                          <div
                            key={produto.id}
                            className="produto-card"
                            onClick={() => abrirProduto(produto)}
                          >
                            <div className="produto-layout">
                              <div className="produto-imagem-section" style={{position:'relative'}}>
                                {false ? (() => {
                                  const embedUrl = getVideoEmbedUrl(produto.video);
                                  const isDirectVideo = embedUrl && embedUrl.match(/\.(mp4|webm|ogg)$/i);
                                  
                                  if (isDirectVideo) {
                                    return (
                                      <video
                                        src={embedUrl}
                                        alt={produto.nome}
                                        muted
                                        loop
                                        autoPlay
                                        playsInline
                                        disablePictureInPicture
                                        controlsList="nodownload nofullscreen noremoteplayback"
                                        className="produto-imagem"
                                        style={{ objectFit: 'cover' }}
                                        onError={(e) => {
                                          e.target.style.display = 'none';
                                          e.target.nextElementSibling.style.display = 'block';
                                        }}
                                      />
                                    );
                                  } else if (embedUrl) {
                                    return (
                                      <iframe
                                        src={embedUrl}
                                        title={produto.nome}
                                        className="produto-imagem"
                                        style={{
                                          border: 'none',
                                          width: '100%',
                                          height: '100%'
                                        }}
                                        allow="autoplay; encrypted-media"
                                        allowFullScreen={false}
                                      />
                                    );
                                  }
                                  return null;
                                })() : null}
                                <img
                                  src={produto.imagem ? `https://grubupload.s3.amazonaws.com/${produto.imagem}` : 'https://grubicons.s3.us-east-1.amazonaws.com/foto-padrao-grub-digital.jpg'}
                                  alt={produto.nome}
                                  className="produto-imagem"
                                  
                                  onError={(e) => {
                                    e.target.src = 'https://grubicons.s3.us-east-1.amazonaws.com/foto-padrao-grub-digital.jpg';
                                  }}
                                />
                                {((produto.em_promocao === true) || (produto.promocao_info?.b_promocao === 1) || (produto.promocao_info?.b_promocao === '1') || (produto.b_promocao === 1) || (produto.b_promocao === '1') || (produto.b_promocao === true)) && (
                                  <div style={{
                                    position:'absolute',
                                    left:0,
                                    right:0,
                                    bottom:0,
                                    background:'#dc2626',
                                    color:'#ffffff',
                                    textAlign:'center',
                                    padding:'6px 0',
                                    fontSize:'0.8rem',
                                    fontWeight:700
                                  }}>EM PROMOÇÃO</div>
                                )}
                              </div>
                              <div className="produto-content">
                                <div className="produto-categoria-label">{String(sub.nome || '').toUpperCase()}</div>
                                <h3 className="produto-titulo">{produto.nome}</h3>
                                <p className="produto-descricao">{produto.descricao}</p>
                                {(() => {
                                  const pricing = calculatePrice(produto, delivery);
                                  return (
                                    <div className="produto-preco">
                                      {pricing.em_promocao
                                        ? (
                                          <>
                                            <div style={{color:'#6b7280', fontWeight:400, fontSize:'0.85rem', textDecoration:'line-through', marginBottom:'2px'}}>
                                              {produto.variacao ? `A partir de ${formatPrice(pricing.valor_original)}` : formatPrice(pricing.valor_original)}
                                            </div>
                                            <div style={{color:'#10b981', fontWeight:700}}>
                                              {produto.variacao ? `A partir de ${formatPrice(pricing.valor)}` : formatPrice(pricing.valor)}
                                            </div>
                                          </>
                                        )
                                        : (
                                          produto.variacao ? `A partir de ${formatPrice(pricing.valor)}` : formatPrice(pricing.valor)
                                        )
                                      }
                                    </div>
                                  );
                                })()}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            );
          })
        ) : (
          <div className="text-center py-5">
            <div style={{ width: '220px', height: '220px', margin: '0 auto' }}>
              <Lottie animationData={buscaProdutosAnimation} loop={true} />
            </div>
            <h4>Nenhum produto encontrado</h4>
            <p className="text-muted">Tente selecionar outra categoria</p>
          </div>
        )}

      </div>

      {/* Footer */}
      <Footer 
        loja={cardapio.loja} 
        onMeusPedidosClick={() => setShowMeusPedidos(true)}
        onLoginClick={() => setShowLogin(true)}
        onCriarContaClick={() => setShowLogin(true)}
      />

      {/* Modal do Produto */}
      {showModal && produtoSelecionado && (
        <ProdutoModal
          produto={produtoSelecionado}
          show={showModal}
          onHide={fecharModal}
        />
      )}

      {/* Offcanvas do Carrinho */}
      <CarrinhoOffcanvas
        show={showCarrinho}
        onHide={fecharCarrinho}
        onOpenMeusPedidos={() => setShowMeusPedidos(true)}
      />
      
      {/* Modal de Login */}
      <LoginModal
        show={showLogin}
        onHide={() => setShowLogin(false)}
        onLogin={handleLogin}
        brandLogoUrl={cardapio.loja?.logo_url}
      />

      {/* Offcanvas de Perfil */}
      <PerfilOffcanvas
        show={showPerfil}
        onHide={() => setShowPerfil(false)}
        usuario={usuario}
        onLogout={handleLogout}
        brand={brand}
        onUserUpdate={handleLogin}
        pedidosNaoEntregues={pedidosPendentes}
      />

      {/* Offcanvas de Meus Pedidos */}
      <MeusPedidosOffcanvas
        show={showMeusPedidos}
        onHide={() => setShowMeusPedidos(false)}
      />
      
      {/* Modal de Upsell */}
      <UpselModal
        show={showUpselModal}
        onHide={() => {
          setShowUpselModal(false);
          abrirCarrinho();
          setShowMeusPedidos(false);
        }}
        products={upsellProducts}
        onSelectProduct={(produto) => {
          setShowUpselModal(false);
          abrirProduto(produto);
        }}
        onGoToCart={() => {
          setShowUpselModal(false);
          abrirCarrinho();
          setShowMeusPedidos(false);
        }}
        loading={loadingUpsells}
      />
      
      {/* Modal do Concierge */}
      <ConciergeModal
        show={showConcierge}
        onHide={() => setShowConcierge(false)}
        onSelectProduct={(produto) => {
          setShowConcierge(false);
          abrirProduto(produto);
        }}
        brand={brand}
      />
      
      {/* Modal de Informações do Balcão */}
      <BalcaoInfoModal
        show={showBalcaoInfo}
        onHide={() => setShowBalcaoInfo(false)}
        onSave={handleSaveBalcaoInfo}
      />
      

      <style>{`@keyframes cart-shake { 0%,100%{ transform: translateX(0);} 20%{ transform: translateX(-3px);} 40%{ transform: translateX(3px);} 60%{ transform: translateX(-2px);} 80%{ transform: translateX(2px);} }`}</style>
      <style>{`
        video::-webkit-media-controls {
          display: none !important;
        }
        video::-webkit-media-controls-panel {
          display: none !important;
        }
        video::-webkit-media-controls-play-button {
          display: none !important;
        }
        video::-webkit-media-controls-start-playback-button {
          display: none !important;
        }
        .produto-card video {
          pointer-events: none;
        }
      `}</style>
      {/* Botão flutuante do carrinho - escondido no modo visualização (cardápio padrão sem mesa/delivery/balcão) */}
      {!modoVisualizacao && !showCarrinho && !showMeusPedidos && !showPerfil && !showModal && !showLogin && !showUpselModal && !showConcierge && !showBalcaoInfo && (
        <div style={{
          position: 'fixed',
          right: '16px',
          bottom: '80px',
          zIndex: 1030
        }}>
          <button
            onClick={abrirCarrinho}
            style={{
              backgroundColor: '#0039ff',
              color: 'white',
              border: 'none',
              borderRadius: '9999px',
              padding: '12px 16px',
              boxShadow: '0 4px 12px rgba(0,57,255,0.25)',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              cursor: 'pointer',
              animation: cartShake ? 'cart-shake 0.6s' : 'none'
            }}
          >
            <Cart />
            <span className="fw-bold">{itens?.length || 0}</span>
          </button>
        </div>
      )}

    </div>
  );
}

export default Cardapio;
